import { Component, h, Prop, Element, Event, EventEmitter, State, Listen } from '@stencil/core';
import { CmsIconConfig, CmsLinkConfig, Logger } from '../../../global/_types';
import { isSmallerThanViewport } from '../../../global/_utils';
import { breakpoints } from '../../../global/_constants';
import { CmsDataPartner } from '../../../global/_data';

@Component({
  tag: 'cms-filter-content-partner',
  styleUrl: 'cms-filter-content-partner.scss',
  shadow: true,
})

export class CmsFilterContentPartner {
  @Element() host: HTMLElement;

  @Prop() config: string | CmsFilterContentPartnerConfig;
  @Prop() customConfig: string | CmsCustomConfigPartner;
  @Prop() showMap: boolean;
  @Prop() rowIndex: number;
  @Prop() type: string;
  @Prop() debug: boolean = false;

  @State() isMobile: boolean = false;

  /* Config */
  private options: CmsFilterContentPartnerConfig;
  private customOptions: CmsCustomConfigPartner;

  private logger: Logger;

  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsFilterContentPartner');
  }

  // Force content update on each render call
  componentWillRender() {
    this.options = typeof (this.config) === 'string'
      ? JSON.parse(this.config) as CmsFilterContentPartnerConfig
      : this.config;

    this.customOptions =
      typeof (this.customConfig) === 'string'
        ? JSON.parse(this.customConfig) as CmsCustomConfigPartner
        : this.customConfig
    ;

    if (this.options.location && this.customOptions.mapsLink && this.customOptions.labels.mapsLink) {
      this.options.mapsLink = {
        link: this.customOptions.mapsLink.concat(this.options.location?.lat, ',', this.options.location?.lng),
        label: this.customOptions.labels.mapsLink,
        linkIcon: 'i-dealer',
        target: '_blank'
      } as CmsLinkConfig;
    }
  }

  componentDidLoad() {
    this.logger.trace('CmsFilterContentEvent loaded');
  }

  /**
   * Process style changes on viewport resize
   * Resize EventHandler
   */
  @Listen('resize', {target: 'window'})
  updateViewport() {
    this.isMobile = isSmallerThanViewport(breakpoints.ml);
  }

  private toggleTooltip(event: MouseEvent, visible: boolean) {
    if (!this.customOptions.showTooltip) {
      return;
    }

    const img = event.target as HTMLElement;
    const tooltip = img.parentElement.parentElement.querySelector('.tooltip');
    if (!tooltip) {
      return;
    }

    if (visible) {
      tooltip.classList.add('visible');
    } else {
      tooltip.classList.remove('visible');
    }
  }

  private getName(name: string) {
    return <div class={`name`}>
      <h5>{name}</h5>
    </div>;
  }

  private getLocation() {
    return <div class={`location`} onClick={this.showMarker.bind(this)}>
      <div>{this.options.address}</div>
      <div>{this.options.zip} {this.options.city}</div>
      <div>{this.options.countryName}</div>
    </div>;
  }

  private getContact(phone: string, email: string, url: string = null) {
    let linkUrl = url ? url.replace(/http:\/\//g, 'https://') : '';
    if (linkUrl !== '' && !linkUrl.startsWith('http')) {
      linkUrl = 'https://' + linkUrl;
    }
    const displayUrl = url ? url.replace(/http[s]:+\/\/(.*)\//gm, '$1') : '';

    return <div class={`contact`}>
      {phone
        ? [
          <div
            class="label">{this.customOptions.labels.phone ? `${this.customOptions.labels.phone}:` : ''}</div>,
          <div class="link"><a href={`tel:${phone}`}>{phone}</a></div>,
        ]
        : ''}
      {email
        ? [
          <div
            class="label">{this.customOptions.labels.email ? `${this.customOptions.labels.email}:` : ''}</div>,
          <div class="link"><a href={`mailto:${email}`}>{email}</a></div>,
        ]
        : ''}
      {url
        ? [
          <div class="label">{this.customOptions.labels.web ? `${this.customOptions.labels.web}:` : ''}</div>,
          <div class="link"><a href={linkUrl} target={`_blank`}>{displayUrl}</a></div>,
        ]
        : ''}
    </div>;
  }

  private getMapLink() {
    return this.options.location?.lat && this.options.location?.lng ?
      <div class="maps-link">
        <cms-link-or-button debug={this.debug} config={this.options.mapsLink} />
      </div>
      : '';
  }

  // Render
  render() {
    const rowStyle = { backgroundColor: this.customOptions.color ? this.customOptions.color : null };
    const cert = this.options.targetGroup && this.options.targetGroup !== '' ? this.customOptions.certifications.find(item => item.color === this.options.targetGroup) : null;

    if (this.type == 'rsm') {
      return ([
        <div class={`headline ${this.type}`}>{this.customOptions.labels.rsm}</div>,
        <div class={`row ${this.rowIndex==0 ? 'first-row' : ''} ${this.type}`} style={rowStyle}>
          {this.getName(this.options.rsmName)}
          {this.getContact(this.options.rsmPhone, this.options.rsmEmail)}
        </div>
      ]);
    } else if (this.type == 'rep') {
      return ([
        <div class={`headline rep`}>{this.customOptions.labels.rep}</div>,
        <div class={`row show-map ${this.type}`} style={rowStyle}>
          {this.getName(this.options.name)}
          {this.getLocation()}
          {this.getContact(this.options.phone, this.options.email, this.options.url)}
          {this.getMapLink()}
        </div>,
      ]);
    } else {
      return (
        <div class={`row ${this.showMap ? 'show-map' : ''} ${this.rowIndex == 0 ? 'first-row' : ''} ${this.type}`}
             style={rowStyle}>
          {this.getName(this.options.name)}
          {this.getLocation()}
          {this.getContact(this.options.phone, this.options.email, this.options.url)}
          <div class="info">
            <div class={`services`}>
              {this.customOptions.services?.length > 0 ?
                this.customOptions.services.map((service: CmsIconConfig) => {
                  const hasTooltip = this.customOptions.showTooltip && service.tooltip && service.tooltip !== '';
                  const iconClass = `icon ${this.options.serviceFilter.includes(service.uid) ? 'active' : 'inactive'} ${hasTooltip ? 'has-tooltip' : ''}`;
                  return (
                    <div class={`service`}>
                      <div class={iconClass}>
                        <img src={service.icon}
                             alt={service.label}
                             onMouseOver={(event) => this.toggleTooltip(event, true)}
                             onMouseLeave={(event) => this.toggleTooltip(event, false)}
                        />
                      </div>
                      {hasTooltip ?
                        <div class={`tooltip`} role="tooltip">
                          <div class="tooltip-arrow"></div>
                          <div class="tooltip-content">{service.tooltip}</div>
                        </div>
                        : ''}
                    </div>
                  );
                })
                : ''}
            </div>
            {cert && cert.icon ?
              <div class={`certificate`}>
                <img src={cert.icon} alt={cert.label} />
              </div>
            : ''}
          </div>
          {this.getMapLink()}
        </div>
      );
    }
  }

  // Events
  @Event({
    eventName: 'showMarker',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  showMarkerEvent: EventEmitter;

  private showMarker() {
    this.showMarkerEvent.emit({ id: this.options._id });
  }
}

export interface CmsFilterContentPartnerConfig extends CmsDataPartner{
  serviceFilter: string[],
  mapsLink: CmsLinkConfig,
  distance: number
}

export interface CmsCustomConfigPartner {
  partnerCountries: {},
  mapsLink: string,
  services: CmsIconConfig[],
  certifications: CmsIconConfig[],
  showTooltip: boolean,
  showRsm: boolean,
  showRep: boolean,
  labels: {
    mapsLink: string,
    phone: string,
    email: string,
    web: string
    rsm: string,
    rep: string
  },
  color?: string
}
